import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { useReviews, http, errMsg } from "../lib/api";
import { Stars } from "./Stars";
import { Reveal } from "./Motion";

const ReviewForm = ({ slug, onDone }) => {
  const [form, setForm] = useState({ name: "", city: "", rating: 0, comment: "" });
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error("Pick a star rating first");
    setBusy(true);
    try {
      await http.post(`/products/${slug}/reviews`, form);
      toast.success("Thanks! Your review is live.");
      qc.invalidateQueries({ queryKey: ["reviews", slug] });
      qc.invalidateQueries({ queryKey: ["product", slug] });
      qc.invalidateQueries({ queryKey: ["products"] });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="review-form" onSubmit={submit} className="card space-y-4 p-6 sm:p-8">
      <p className="t-h4">How did the build go?</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <button type="button" key={i} data-testid={`review-star-${i}`} onClick={() => setForm({ ...form, rating: i })} aria-label={`${i} stars`}>
            <Star className={`h-8 w-8 transition-transform hover:scale-110 ${i <= form.rating ? "fill-brass text-brass" : "text-brass/40"}`} />
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input data-testid="review-name-input" required minLength={2} placeholder="Your name (e.g. Asha & Dev)" value={form.name} onChange={set("name")} className="input" />
        <input data-testid="review-city-input" placeholder="Locality (e.g. Gachibowli)" value={form.city} onChange={set("city")} className="input" />
      </div>
      <textarea data-testid="review-comment-input" required minLength={10} rows={4} placeholder="How was the build? How is it holding up?" value={form.comment} onChange={set("comment")} className="input h-auto py-3" />
      <button data-testid="review-submit-button" disabled={busy} className="btn-sheesham">{busy ? "Posting" : "Post review"}</button>
    </motion.form>
  );
};

export const Reviews = ({ product }) => {
  const { data: reviews = [] } = useReviews(product.slug);
  const [writing, setWriting] = useState(false);
  return (
    <section data-testid="reviews-section" id="reviews" className="grid gap-10 lg:grid-cols-12">
      <Reveal className="lg:col-span-4">
        <p className="eyebrow">Reviews</p>
        <h2 className="t-h2 mt-3">From homes that built it</h2>
        <div className="card mt-6 inline-flex items-center gap-5 p-5">
          <span data-testid="review-average" className="price text-5xl">{product.rating || "–"}</span>
          <div>
            <Stars value={product.rating} size={18} />
            <p data-testid="review-count" className="t-small mt-1">{product.review_count} reviews</p>
          </div>
        </div>
        {!writing && <div><button data-testid="write-review-button" onClick={() => setWriting(true)} className="btn-outline mt-6">Write a review</button></div>}
      </Reveal>
      <div className="space-y-6 lg:col-span-8">
        {writing && <ReviewForm slug={product.slug} onDone={() => setWriting(false)} />}
        {reviews.length === 0 && !writing && <div className="card border-dashed p-8 text-center"><p className="t-h4">No reviews yet.</p><p className="t-small mt-1">Be the first to tell others how the build went.</p></div>}
        <ul className="grid gap-4 sm:grid-cols-2">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={Math.min(i, 5) * 0.05} as="li" data-testid="review-item" className="card p-6">
              <div className="flex items-center justify-between gap-4">
                <Stars value={r.rating} />
                <span className="t-small">{new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
              <p className="t-body mt-3">{r.comment}</p>
              <p className="mt-3 text-sm font-semibold">{r.name}{r.city && <span className="font-normal text-muted-foreground">, {r.city}</span>}</p>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};
